import mapboxgl from 'mapbox-gl';
import { LatLng } from '../../../utils/types';
import {
  reverseGeocodeNearestAddress,
  ReverseGeocodedAddress,
} from '../../../HouseMap/BuildingMap.helpers';
import { resolvePrimaryAddress } from '../../../HouseMap/sideAddresses';
import { fetchSideAddressesWithDeadline } from '../../../HouseMap/fetchSideAddressesWithDeadline';

export type ListingBuildingGeometry = {
  type: string;
  coordinates: number[][][] | number[][][][] | number[];
};

// Structured address the backend stores on the off-market willingness doc and
// uses to build the listing's geometry (from `coordinates`). Mirrors the
// `addressObject` shape assembled in LandingPageCouldBuyOrCouldSellModal.
export interface ListingAddressObject {
  country: string;
  region: string;
  city: string;
  postcode: string;
  street: string;
  houseNumber: string;
  coordinates: { lat: number; lng: number };
}

export interface ResolvedListingBuilding {
  // Mapbox building feature id, or null when no building was found at the
  // coordinate (the caller substitutes a temporary id, matching the old flow).
  buildingId: string | null;
  // Building footprint polygon when available.
  geometry: ListingBuildingGeometry | null;
  // Structured address resolved at the coordinate, or null if reverse geocoding
  // failed.
  addressObject: ListingAddressObject | null;
}

const BUILDING_LOOKUP_RADIUS_PX = 80;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isFiniteNum = (n: any) => typeof n === 'number' && Number.isFinite(n);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidLngLat = (lng: any, lat: any) =>
  isFiniteNum(lng) && isFiniteNum(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;

// Validate every coordinate pair before sending geometry to the indexed
// buildings collection; malformed GeoJSON can make Mongo reject the insert.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validateFootprintGeometry = (geometry: any): geometry is ListingBuildingGeometry => {
  try {
    const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    if (!Array.isArray(polygons) || polygons.length === 0) return false;
    for (const poly of polygons) {
      if (!Array.isArray(poly) || poly.length === 0) return false;
      for (const ring of poly) {
        if (!Array.isArray(ring) || ring.length < 4) return false;
        for (const pair of ring) {
          if (!Array.isArray(pair) || !isValidLngLat(pair[0], pair[1])) return false;
        }
      }
    }
    return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
  } catch {
    return false;
  }
};

const geometryCentroid = (geometry: GeoJSON.Geometry): [number, number] | null => {
  const outerRings: number[][][] = [];
  if (geometry.type === 'Polygon') {
    outerRings.push(geometry.coordinates[0] as number[][]);
  } else if (geometry.type === 'MultiPolygon') {
    (geometry.coordinates as number[][][][]).forEach((poly) => {
      if (poly[0]) outerRings.push(poly[0] as number[][]);
    });
  } else {
    return null;
  }
  let sx = 0;
  let sy = 0;
  let n = 0;
  outerRings.forEach((ring) => {
    ring.forEach((pair) => {
      if (isFiniteNum(pair[0]) && isFiniteNum(pair[1])) {
        sx += pair[0];
        sy += pair[1];
        n += 1;
      }
    });
  });
  return n === 0 ? null : [sx / n, sy / n];
};

const mergeBuildingFootprint = (
  map: mapboxgl.Map,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  featureId: any,
  fallbackGeometry?: GeoJSON.Geometry | null,
): ListingBuildingGeometry | null => {
  // Each entry is a full polygon (array-of-rings): ring[0] is the outer ring and
  // any subsequent rings are holes. Preserving this structure keeps holes and
  // multi-polygon grouping intact through the union below.
  let polygons: number[][][][] = [];
  try {
    const slices = map.queryRenderedFeatures({
      layers: ['building'],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filter: ['==', ['id'], featureId as any],
    });
    slices.forEach((slice) => {
      if (slice.geometry?.type === 'Polygon') {
        polygons.push(slice.geometry.coordinates as number[][][]);
      } else if (slice.geometry?.type === 'MultiPolygon') {
        (slice.geometry.coordinates as number[][][][]).forEach((poly) => {
          polygons.push(poly);
        });
      }
    });
  } catch {
    // Fall back to the clicked/queried feature geometry below.
  }

  if (polygons.length === 0 && fallbackGeometry?.type === 'Polygon') {
    polygons = [(fallbackGeometry as GeoJSON.Polygon).coordinates as number[][][]];
  }
  if (polygons.length === 0) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let buildingGeometry: any;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const pc = require('polygon-clipping');
    const unionResult: number[][][][] = pc.union(...polygons);
    buildingGeometry = unionResult.length === 1
      ? { type: 'Polygon', coordinates: unionResult[0] }
      : { type: 'MultiPolygon', coordinates: unionResult };
  } catch {
    buildingGeometry = polygons.length > 1
      ? { type: 'MultiPolygon', coordinates: polygons }
      : { type: 'Polygon', coordinates: polygons[0] };
  }

  return validateFootprintGeometry(buildingGeometry) ? buildingGeometry : null;
};

const findBuildingFeatureAtCoord = (
  map: mapboxgl.Map,
  latLng: LatLng,
): mapboxgl.MapboxGeoJSONFeature | undefined => {
  const point = map.project([latLng.lng, latLng.lat]);
  const exact = map.queryRenderedFeatures(point, { layers: ['building'] })[0];
  if (exact) return exact;

  const nearby = map.queryRenderedFeatures(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [[point.x - BUILDING_LOOKUP_RADIUS_PX, point.y - BUILDING_LOOKUP_RADIUS_PX], [point.x + BUILDING_LOOKUP_RADIUS_PX, point.y + BUILDING_LOOKUP_RADIUS_PX]] as any,
    { layers: ['building'] },
  );
  let best: { feature: mapboxgl.MapboxGeoJSONFeature; distance: number } | null = null;
  nearby.forEach((feature) => {
    const centroid = feature.geometry ? geometryCentroid(feature.geometry as GeoJSON.Geometry) : null;
    if (!centroid) return;
    const dx = centroid[0] - latLng.lng;
    const dy = centroid[1] - latLng.lat;
    const distance = dx * dx + dy * dy;
    if (!best || distance < best.distance) best = { feature, distance };
  });
  return best?.feature;
};

// Query the Mapbox `building` layer at a coordinate using a hidden, throwaway
// map (ported from LandingPageCouldBuyOrCouldSellModal's `queryBuildingAtLocation`)
// to derive the canonical building id + footprint geometry. Resolves with
// `{ id: null, geometry: null }` on any failure so the publish flow never hangs.
const queryBuildingAtLocation = (
  latLng: LatLng,
): Promise<{ id: string | null; geometry: ListingBuildingGeometry | null }> =>
  new Promise((resolve) => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';

    const tempContainer = document.createElement('div');
    tempContainer.style.width = '100px';
    tempContainer.style.height = '100px';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.position = 'absolute';
    document.body.appendChild(tempContainer);

    let isCleanedUp = false;
    let tempMap: mapboxgl.Map | null = null;
    const cleanup = () => {
      if (isCleanedUp) return;
      isCleanedUp = true;
      try {
        tempMap?.remove();
      } catch {
        // Ignore errors during map removal
      }
      try {
        if (document.body.contains(tempContainer)) {
          document.body.removeChild(tempContainer);
        }
      } catch {
        // Ignore errors during container removal
      }
    };

    try {
      tempMap = new mapboxgl.Map({
        container: tempContainer,
        style: `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`,
        center: [latLng.lng, latLng.lat],
        // High zoom so building features are loaded and queryable.
        zoom: 17,
        interactive: false,
      });
    } catch {
      cleanup();
      resolve({ id: null, geometry: null });
      return;
    }

    // Never hang the publish flow if the tiles never settle.
    const timer = window.setTimeout(() => {
      cleanup();
      resolve({ id: null, geometry: null });
    }, 8000);

    tempMap.on('idle', () => {
      const map = tempMap;
      if (!map) {
        window.clearTimeout(timer);
        cleanup();
        resolve({ id: null, geometry: null });
        return;
      }

      let buildingId: string | null = null;
      let buildingGeometry: ListingBuildingGeometry | null = null;

      if (map.getLayer('building')) {
        const buildingFeature = findBuildingFeatureAtCoord(map, latLng);
        if (buildingFeature?.id !== undefined && buildingFeature.id !== null) {
          buildingId = buildingFeature.id.toString();
          buildingGeometry = mergeBuildingFootprint(
            map,
            buildingFeature.id,
            buildingFeature.geometry as GeoJSON.Geometry,
          );
        }
      }

      window.clearTimeout(timer);
      cleanup();
      resolve({ id: buildingId, geometry: buildingGeometry });
    });

    tempMap.on('error', () => {
      window.clearTimeout(timer);
      cleanup();
      resolve({ id: null, geometry: null });
    });
  });

const toAddressObject = (
  addr: ReverseGeocodedAddress | null,
  latLng: LatLng,
): ListingAddressObject | null => {
  if (!addr) return null;
  return {
    country: addr.country,
    region: addr.region,
    city: addr.city,
    postcode: addr.postcode,
    street: addr.street,
    houseNumber: addr.houseNumber,
    // Always pin the coordinates to the resolved listing location so the
    // backend builds the listing geometry from the same point the user picked.
    coordinates: { lat: latLng.lat, lng: latLng.lng },
  };
};

// Resolve everything the `/off-market-willingness` POST needs from a single
// coordinate: the Mapbox building id + footprint geometry, and the structured
// address. Runs the Mapbox query and reverse geocode in parallel, then checks
// the nearest address against the footprint the way the building card does,
// so a building with no address point of its own never takes a neighbour's
// address (JAA-410).
export const resolveListingBuilding = async (
  latLng: LatLng,
): Promise<ResolvedListingBuilding> => {
  const [building, reverseGeocoded] = await Promise.all([
    queryBuildingAtLocation(latLng),
    reverseGeocodeNearestAddress(latLng),
  ]);
  // Address refinement must not leave submission waiting on a stalled provider.
  const sides = await fetchSideAddressesWithDeadline(
    latLng,
    building.geometry,
    process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '',
  );

  return {
    buildingId: building.id,
    geometry: building.geometry,
    addressObject: toAddressObject(
      resolvePrimaryAddress(reverseGeocoded, sides, latLng),
      latLng,
    ),
  };
};
